import { Download, Eye, FileText, Trash2, Upload } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  DocumentPreviewDialog,
  type PreviewDoc,
} from "@/features/documents/components/document-preview-dialog"
import { DocumentUploadDialog } from "@/features/documents/components/document-upload-dialog"
import {
  useDeleteDocument,
  useDownloadDocument,
} from "@/features/documents/hooks/use-document-mutations"
import { formatDate } from "@/lib/format"
import type { ContractDocument, DocumentLevel } from "@/types/domain"

interface DocumentsListProps {
  documents: ContractDocument[] | null | undefined
  level: Extract<DocumentLevel, "contract" | "order" | "delivery">
  contractId?: string
  orderId?: string
  deliveryId?: string
  invalidateKeys?: readonly (readonly unknown[])[]
  title?: string
  allowUpload?: boolean
}

export function DocumentsList({
  documents,
  level,
  contractId,
  orderId,
  deliveryId,
  invalidateKeys,
  title,
  allowUpload = true,
}: DocumentsListProps) {
  const { t, i18n } = useTranslation(["documents", "common"])
  const [uploadOpen, setUploadOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ContractDocument | null>(null)
  const [previewDoc, setPreviewDoc] = useState<PreviewDoc | null>(null)

  const { download } = useDownloadDocument()
  const remove = useDeleteDocument({
    invalidateKeys,
    onSuccess: () => setDeleteTarget(null),
  })

  const list = documents ?? []

  function handleDownload(doc: ContractDocument) {
    download(
      {
        type: level,
        documentKey: doc.key,
        contractId,
        orderId,
        deliveryId,
      },
      doc.title || doc.key,
    )
  }

  function handlePreview(doc: ContractDocument) {
    setPreviewDoc({
      name: doc.title || doc.key,
      key: doc.key,
      uploadedAt: doc.uploadedAt,
      request: {
        type: level,
        documentKey: doc.key,
        contractId,
        orderId,
        deliveryId,
      },
    })
  }

  function confirmDelete() {
    if (!deleteTarget) return
    remove.mutate({
      level,
      documentKey: deleteTarget.key,
      contractId,
      orderId,
      deliveryId,
    })
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="text-muted-foreground size-4" />
          {title ?? t("title")}
        </CardTitle>
        {allowUpload ? (
          <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)}>
            <Upload className="size-4" />
            {t("uploadButton")}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {list.length === 0 ? (
          <EmptyState title={t("empty")} />
        ) : (
          <ul className="divide-border divide-y">
            {list.map((doc) => (
              <li
                key={doc.key}
                className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => handlePreview(doc)}
                    className="truncate text-left text-sm font-medium hover:underline"
                  >
                    {doc.title || doc.key}
                  </button>
                  {doc.description ? (
                    <p className="text-muted-foreground truncate text-xs">
                      {doc.description}
                    </p>
                  ) : null}
                  {doc.uploadedAt ? (
                    <p className="text-muted-foreground text-xs">
                      {formatDate(doc.uploadedAt, i18n.language)}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => handlePreview(doc)}
                    aria-label={t("preview.btn")}
                  >
                    <Eye className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDownload(doc)}
                    aria-label={t("actions.download", { ns: "common" })}
                  >
                    <Download className="size-4" />
                  </Button>
                  {allowUpload ? (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => setDeleteTarget(doc)}
                      aria-label={t("actions.delete", { ns: "common" })}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <DocumentPreviewDialog
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />

      <DocumentUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        level={level}
        contractId={contractId}
        orderId={orderId}
        deliveryId={deliveryId}
        invalidateKeys={invalidateKeys}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.title || deleteTarget?.key}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              {t("actions.cancel", { ns: "common" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                confirmDelete()
              }}
              disabled={remove.isPending}
            >
              {remove.isPending
                ? t("actions.deleting", { ns: "common" })
                : t("actions.delete", { ns: "common" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
